import { isPlatformBrowser } from "@angular/common";
import { inject, isDevMode, signal, WritableSignal } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { LocalStorageService } from "@app/core";
import { SessionDto } from "@app/core/dto/session.dto";
import { TrackingDto } from "@app/core/dto/tracking-data.dto";
import { UserContext } from "@app/core/dto/user-context-cart.dto";
import { CacheStatusEnum } from "@app/core/enums/cache-status.enum";
import { ModalType } from "@app/core/enums/modal.enums";
import { SessionStorageKeyTypes, SessionStorageValueTypes } from "@app/core/enums/session-storage-key-type.enum";
import { DynamicImportService } from "@app/core/services/dynamic-import/dynamic-import.service";
import { BaseWidget } from "@app/widgets/base-widget/base-widget";
import { PaymentGatewayMapper } from "./rule-validator/payment-gateway-mapper";
import { StringUtils } from "@app/core/utils/string.utils";

export class HandleDataResponse extends BaseWidget {
    actionResponseContextSignal: WritableSignal<any> = signal({});
    private readonly _localStorageService: LocalStorageService = inject(LocalStorageService);

    constructor(private renderEngineComponent: any) {
        super();
    }

    async handleResponse(
        res,
        response?: {
            slots: any;
            actionResponseContext: any;
            redirectionUrl: any;
            footer: any;
            header: any;
            initPaymentInfo: any;
            userContext: UserContext;
            pageData: any;
        },
        session?: SessionDto,
    ): Promise<void> {
        if (isDevMode()) {
            console.log('Api Response Time: ', new Date().getTime() - this.renderEngineComponent.apiInitTime);
        }

        this.actionResponseContextSignal.set(response?.actionResponseContext);
        if (response?.initPaymentInfo) {
            return this._handlePaymentResponse(response?.initPaymentInfo);
        }

        const reviewPopupInfo: Record<string, any> =
            response?.pageData?.reviewPopup;

        if (isPlatformBrowser(this.platformId) && reviewPopupInfo?.visible) {
            const reviewPopupClosedByUser: string =
                await this.renderEngineComponent.sessionStorageService.get(
                    SessionStorageKeyTypes.reviewPopupClosedByUser,
                );

            if (reviewPopupClosedByUser != SessionStorageValueTypes.closed) {
                const reviewPopupDialogRef: MatDialogRef<any, any> = this._dialog.open(
                    await this.dyis.dynamicModals.get(ModalType.WRITE_A_REVIEW_POPUP),
                    {
                        disableClose: true,
                        width: 'auto',
                        hasBackdrop: false,
                        data: reviewPopupInfo?.data || {},
                    },
                );

                reviewPopupDialogRef?.afterClosed().subscribe((action) => {
                    if (action) {
                        this.renderEngineComponent.onActionCall({
                            actionData: action,
                        });
                    }
                });
            }
        }

        const pageType: string =
            response?.pageData?.trackingContext?.page_type ||
            this.renderEngineComponent.apiBodyData()?.pageType;

        if (this.getCacheStatus() === CacheStatusEnum.MISS) {
            if (isPlatformBrowser(this.platformId) && !this.shouldSkipHydration()) {
                this._loadUserDetails(res, false, session, response?.userContext);
                this.renderEngineComponent.analyticsService.setPageType(pageType);
                this.renderEngineComponent.analyticsService.init();
            }
        } else {
            if (isPlatformBrowser(this.platformId) && !this.shouldSkipHydration()) {
                this._loadUserDetails(res, true, session, response?.userContext);
                this.renderEngineComponent.analyticsService.setPageType(pageType);
                this.renderEngineComponent.analyticsService.init();
            }
        }

        const trackingData: TrackingDto = response?.pageData?.trackingContext;
        if (trackingData) {
            this.renderEngineComponent.analyticsService?.pushTrackingEvent(trackingData);
        }

        if (response?.actionResponseContext) {
            for (let i in response?.actionResponseContext?.actionMessages) {
                if (
                    response?.actionResponseContext?.actionMessages[i]
                        ?.actionMessageType &&
                    response?.actionResponseContext?.actionMessages[i]
                        ?.actionMessageType === 'TEXT_WITH_BUTTON'
                ) {
                    this.renderEngineComponent.snackBar
                        .open(
                            response?.actionResponseContext?.actionMessages[i]?.text,
                            response?.actionResponseContext?.actionMessages[i]?.buttons[0]
                                ?.text,
                            {
                                duration: 3000,
                            },
                        )
                        .onAction()
                        .subscribe(() => {
                            this.renderEngineComponent.router.navigateByUrl(
                                response?.actionResponseContext?.actionMessages[i]?.buttons[0]
                                    ?.link,
                            );
                        });
                } else if (
                    response?.actionResponseContext?.actionMessages[i]
                        ?.actionMessageType === 'DEFAULT'
                ) {
                    this.renderEngineComponent.snackBar.open(
                        response?.actionResponseContext?.actionMessages[i]?.text,
                        null,
                        {
                            duration: 3000,
                        },
                    );
                }
            }
        }

        if (response?.redirectionUrl && typeof window != 'undefined') {
            window.location.href = response?.redirectionUrl;
            return;
        }

        if (response?.slots?.length) {
            response?.slots?.forEach((slot: any) => {
                if (slot?.id) {
                    this.renderEngineComponent.widgetsService.cssClassMap.set(
                        slot.id,
                        this.getClass(slot?.layoutParams ?? {}),
                    );
                    this.renderEngineComponent.widgetsService.cssStylesMap.set(
                        slot.id,
                        this.getStyles(
                            slot?.layoutParams ?? {},
                            slot?.widget?.widgetAttributes ?? {},
                        ),
                    );
                }
            });
            this.renderEngineComponent.slots.set(response?.slots);
        }
        if (response?.footer?.slots?.length) {
            this.renderEngineComponent.footerSlots.set(response?.footer?.slots);
            this.renderEngineComponent.sessionStorageService.set('footer', response?.footer);
        }
        if (response?.header?.slots?.length) {
            response?.header?.slots?.forEach((slot: any) => {
                if (slot?.id) {
                    this.renderEngineComponent.widgetsService.cssClassMap.set(
                        slot.id,
                        this.getClass(slot?.layoutParams ?? {}),
                    );
                    this.renderEngineComponent.widgetsService.cssStylesMap.set(
                        slot.id,
                        this.getStyles(
                            slot?.layoutParams ?? {},
                            slot?.widget?.widgetAttributes ?? {},
                        ),
                    );
                }
            });
            this.renderEngineComponent.headerSlots.set(response?.header?.slots);
            this.renderEngineComponent.sessionStorageService.set('header', response?.header);
        }

        this._loadRequiredComponents();
    }

    private _loadUserDetails(
        response,
        isLazy: boolean,
        userSession?: SessionDto,
        userContext?: UserContext,
    ): void {
        if (isLazy) {
            this.getUserDetails(this.renderEngineComponent.apiBodyData()).subscribe((res) => {
                this._bindUserDetails(res, res?.SESSION, res?.RESPONSE?.userContext);
            });
        } else {
            this._bindUserDetails(response, userSession, userContext);
        }
    }

    private async _handlePaymentResponse(initPaymentInfo: any): Promise<void> {
        this.renderEngineComponent.isLoading.set(true);
        const cls: any = PaymentGatewayMapper.get(initPaymentInfo?.paymentGateway);
        const dialog = this._dialog
        const dyis: DynamicImportService = this.dyis
        return new cls({
            dialog,
            dyis
        }).handle(initPaymentInfo);
    }

    private async _loadRequiredComponents(): Promise<void> {
        if (isPlatformBrowser(this.platformId) && !this.shouldSkipHydration()) {
            this.dyis.init();
            this.renderEngineComponent.fontLoaderService.loadMaterialIcons();
            this.renderEngineComponent.loadComponents(true);
            this.renderEngineComponent.fetchCloudflareHeaders();
        }
    }

    private _bindUserDetails(
        response,
        userSession?: SessionDto,
        userContext?: UserContext,
    ): void {
        this._setCookie(response);
        this.renderEngineComponent.userContext.set(userContext);
        this.renderEngineComponent.userSession.set(userSession);
        this._localStorageService.setItem(
            'isLoggedIn',
            this.renderEngineComponent.userSession()?.isLoggedIn,
        );
        this._localStorageService.setItem(
            'sessionCount',
            this.renderEngineComponent.userContext()?.userCart?.itemCount,
        );
        this.renderEngineComponent.analyticsService?.setUserSession(this.renderEngineComponent.userSession());
        this.renderEngineComponent.analyticsService?.setUserContext(this.renderEngineComponent.userContext());
    }

    private _setCookie(res) {
        const metaInfo: Record<string, any> = res?.META_INFO;

        if (metaInfo?.abContext?.abWrapper) {
            const abWrapper: Record<string, any> = metaInfo.abContext.abWrapper;

            if (isPlatformBrowser(this.platformId)) {
                abWrapper.forEach((entry: any) => {
                    Object.entries(entry).forEach(([key, value]) => {
                        const cookieValue: string = this.cookieService.getCookie(key);
                        const parsedValue: {
                            abId: string;
                            cookieTimeout?: number;
                        } = value as { abId: string, cookieTimeout?: number };
                        if (StringUtils.isEmpty(cookieValue) && !StringUtils.isEmpty(parsedValue?.abId)) {
                            this.cookieService.setCookie(key, parsedValue?.abId, parsedValue?.cookieTimeout);
                        }
                    });
                });
            }
        }

    }

}